import React, { useEffect, useMemo, useRef, useState } from "react";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { FaFilm, FaImages, FaPhotoVideo, FaUserAlt, FaUserTie } from "react-icons/fa";
import { ensurePanelAdminSession } from "../utils/panelSession";

function defaultFeedVisibility() {
  return {
    userViewer: {
      allUsers: true,
      allArtists: true,
      followingUsers: true,
      followingArtists: true,
      interestUsers: true,
      interestArtists: true,
    },
    artistViewer: {
      allUsers: true,
      allArtists: true,
      followingUsers: true,
      followingArtists: true,
      interestUsers: true,
      interestArtists: true,
    },
  };
}

function defaults() {
  return {
    contentFeed: {
      post: defaultFeedVisibility(),
      reel: defaultFeedVisibility(),
      story: defaultFeedVisibility(),
    },
  };
}

function mergeSettingsShape(raw = {}) {
  const base = defaults();
  return {
    ...base,
    ...raw,
    contentFeed: {
      post: { ...base.contentFeed.post, ...((raw.contentFeed || {}).post || {}) },
      reel: { ...base.contentFeed.reel, ...((raw.contentFeed || {}).reel || {}) },
      story: { ...base.contentFeed.story, ...((raw.contentFeed || {}).story || {}) },
    },
  };
}

function safeEmployee() {
  try {
    const raw = localStorage.getItem("employee");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

const CONTENT_SECTIONS = [
  { key: "post", title: "Posts", icon: <FaPhotoVideo /> },
  { key: "story", title: "Stories", icon: <FaImages /> },
  { key: "reel", title: "Reels", icon: <FaFilm /> },
];

const VIEWER_CARDS = [
  { key: "userViewer", title: "User Viewer", icon: <FaUserAlt /> },
  { key: "artistViewer", title: "Artist Viewer", icon: <FaUserTie /> },
];

const TOGGLES = [
  { key: "allUsers", label: "See all user content" },
  { key: "allArtists", label: "See all artist content" },
  { key: "followingUsers", label: "See followed users" },
  { key: "followingArtists", label: "See followed artists" },
  { key: "interestUsers", label: "Show interest-matched user content" },
  { key: "interestArtists", label: "Show interest-matched artist content" },
];

export default function HandlePostStoryReel() {
  const employee = useMemo(() => safeEmployee(), []);
  const role = String(employee?.role || "").toLowerCase();
  const isSuperAdmin = role === "super_admin";
  const perms = Array.isArray(employee?.permissions) ? employee.permissions : [];
  const canView = isSuperAdmin || perms.includes("view_settings") || perms.includes("manage_settings");
  const canManage = isSuperAdmin || perms.includes("manage_settings");

  const app = useMemo(() => {
    try {
      return getApp();
    } catch {
      return null;
    }
  }, []);
  const functions = useMemo(() => (app ? getFunctions(app, "asia-south1") : null), [app]);
  const [settings, setSettings] = useState(defaults());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState({ type: "", text: "" });
  const noticeTimerRef = useRef(null);

  const adminContext = useMemo(() => ({
    docId: String(employee?.docId || ""),
    role: String(employee?.role || ""),
    mobile: String(employee?.mobile || ""),
  }), [employee?.docId, employee?.mobile, employee?.role]);

  const ping = (text, type = "ok") => {
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    setNotice({ text, type });
    noticeTimerRef.current = setTimeout(() => setNotice({ text: "", type: "" }), 3500);
  };

  useEffect(() => () => {
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
  }, []);

  const call = async (name, payload = {}) => {
    if (!functions) throw new Error("Cloud Functions not initialized");
    const { employee: sessionEmployee } = await ensurePanelAdminSession({ forceRefresh: true });
    const fn = httpsCallable(functions, name);
    const res = await fn({
      ...payload,
      adminContext: {
        ...adminContext,
        docId: String(sessionEmployee?.docId || adminContext.docId || ""),
        role: String(sessionEmployee?.role || adminContext.role || ""),
        mobile: String(sessionEmployee?.mobile || adminContext.mobile || ""),
      },
    });
    return res?.data || {};
  };

  useEffect(() => {
    let active = true;
    (async () => {
      if (!canView) {
        setLoading(false);
        return;
      }
      try {
        if (!functions) throw new Error("Cloud Functions not initialized");
        const { employee: sessionEmployee } = await ensurePanelAdminSession({ forceRefresh: true });
        const fn = httpsCallable(functions, "getPanelSettings");
        const res = await fn({
          adminContext: {
            ...adminContext,
            docId: String(sessionEmployee?.docId || adminContext.docId || ""),
            role: String(sessionEmployee?.role || adminContext.role || ""),
            mobile: String(sessionEmployee?.mobile || adminContext.mobile || ""),
          },
        });
        const data = res?.data || {};
        if (!active) return;
        setSettings(mergeSettingsShape(data?.settings || {}));
      } catch (e) {
        if (!active) return;
        ping(e?.message || "Failed to load content feed settings", "err");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [adminContext, canView, functions]);

  const updateToggle = (contentKey, viewerKey, toggleKey, checked) => {
    setSettings((prev) => ({
      ...prev,
      contentFeed: {
        ...prev.contentFeed,
        [contentKey]: {
          ...prev.contentFeed[contentKey],
          [viewerKey]: {
            ...prev.contentFeed[contentKey][viewerKey],
            [toggleKey]: checked,
          },
        },
      },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await call("updatePanelSettings", {
        updates: { contentFeed: settings.contentFeed },
      });
      setSettings(mergeSettingsShape(res?.settings || settings));
      ping("Post / Story / Reel visibility rules updated.");
    } catch (e) {
      ping(e?.message || "Failed to save content feed settings", "err");
    } finally {
      setSaving(false);
    }
  };

  if (!canView) {
    return <div style={sx.center}>You do not have access to this page.</div>;
  }

  if (loading) {
    return <div style={sx.center}>Loading content feed settings...</div>;
  }

  return (
    <div style={sx.page}>
      <div style={sx.hero}>
        <div>
          <div style={sx.eyebrow}>Feed Control</div>
          <h1 style={sx.title}>Handle Post, Story, Reel</h1>
          <p style={sx.copy}>
            Decide what users and artists can see in the app feed. Any enabled rule can make a content item visible.
          </p>
        </div>
        <button style={{ ...sx.saveBtn, ...(saving ? sx.saveBtnBusy : {}) }} onClick={save} disabled={!canManage || saving}>
          {saving ? "Saving..." : "Save feed rules"}
        </button>
      </div>

      {notice.text ? (
        <div style={{ ...sx.notice, ...(notice.type === "err" ? sx.noticeErr : sx.noticeOk) }}>
          {notice.text}
        </div>
      ) : null}

      <div style={sx.grid}>
        {CONTENT_SECTIONS.map((section) => (
          <section key={section.key} style={sx.sectionCard}>
            <div style={sx.sectionHead}>
              <div style={sx.sectionIcon}>{section.icon}</div>
              <div>
                <h2 style={sx.sectionTitle}>{section.title}</h2>
                <p style={sx.sectionCopy}>
                  Controls for {section.title.toLowerCase()} feed visibility.
                </p>
              </div>
            </div>

            <div style={sx.viewerGrid}>
              {VIEWER_CARDS.map((viewer) => {
                const current = settings.contentFeed?.[section.key]?.[viewer.key] || defaultFeedVisibility().userViewer;
                return (
                  <div key={viewer.key} style={sx.viewerCard}>
                    <div style={sx.viewerHead}>
                      <div style={sx.viewerIcon}>{viewer.icon}</div>
                      <div style={sx.viewerTitle}>{viewer.title}</div>
                    </div>
                    <div style={sx.toggleList}>
                      {TOGGLES.map((toggle) => (
                        <label key={toggle.key} style={sx.toggleRow}>
                          <span style={sx.toggleLabel}>{toggle.label}</span>
                          <input
                            type="checkbox"
                            checked={Boolean(current[toggle.key])}
                            disabled={!canManage || saving}
                            onChange={(e) => updateToggle(section.key, viewer.key, toggle.key, e.target.checked)}
                          />
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

const sx = {
  page: {
    padding: 24,
    display: "grid",
    gap: 18,
  },
  center: {
    minHeight: "60vh",
    display: "grid",
    placeItems: "center",
    color: "#334155",
    fontSize: 16,
    fontWeight: 700,
  },
  hero: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    padding: 24,
    borderRadius: 22,
    background: "linear-gradient(135deg, #fff6f8 0%, #fff1e8 100%)",
    border: "1px solid #f4d8df",
  },
  eyebrow: {
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.2,
    textTransform: "uppercase",
    color: "#be185d",
    marginBottom: 6,
  },
  title: {
    margin: 0,
    fontSize: 30,
    lineHeight: 1.1,
    color: "#111827",
  },
  copy: {
    margin: "10px 0 0",
    maxWidth: 720,
    color: "#475569",
    lineHeight: 1.5,
    fontSize: 14,
  },
  saveBtn: {
    border: 0,
    borderRadius: 14,
    padding: "12px 18px",
    background: "linear-gradient(135deg, #e11d48 0%, #f97316 100%)",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer",
    minWidth: 170,
  },
  saveBtnBusy: {
    opacity: 0.7,
    cursor: "wait",
  },
  notice: {
    padding: "12px 14px",
    borderRadius: 14,
    fontWeight: 700,
    fontSize: 14,
  },
  noticeOk: {
    background: "#ecfdf5",
    border: "1px solid #86efac",
    color: "#166534",
  },
  noticeErr: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#991b1b",
  },
  grid: {
    display: "grid",
    gap: 18,
  },
  sectionCard: {
    background: "#fff",
    border: "1px solid #f1e6ec",
    borderRadius: 22,
    padding: 20,
    boxShadow: "0 14px 36px rgba(17,24,39,0.06)",
  },
  sectionHead: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    marginBottom: 16,
  },
  sectionIcon: {
    width: 44,
    height: 44,
    borderRadius: 14,
    display: "grid",
    placeItems: "center",
    background: "linear-gradient(135deg, #fdf2f8 0%, #ffedd5 100%)",
    color: "#e11d48",
    fontSize: 18,
  },
  sectionTitle: {
    margin: 0,
    color: "#111827",
    fontSize: 22,
  },
  sectionCopy: {
    margin: "4px 0 0",
    color: "#64748b",
    fontSize: 13,
  },
  viewerGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))",
    gap: 14,
  },
  viewerCard: {
    borderRadius: 18,
    padding: 16,
    border: "1px solid #f5dbe3",
    background: "#fffafc",
  },
  viewerHead: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 14,
  },
  viewerIcon: {
    width: 34,
    height: 34,
    borderRadius: 12,
    display: "grid",
    placeItems: "center",
    background: "#ffffff",
    color: "#d81b60",
    border: "1px solid #f1d3de",
  },
  viewerTitle: {
    color: "#111827",
    fontSize: 16,
    fontWeight: 800,
  },
  toggleList: {
    display: "grid",
    gap: 10,
  },
  toggleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "10px 12px",
    borderRadius: 14,
    background: "#fff",
    border: "1px solid #f3e4ea",
  },
  toggleLabel: {
    color: "#334155",
    fontSize: 14,
    fontWeight: 600,
  },
};
